import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AccessDenied, errorStatus } from '@/components/RouteError';
import { usePageHeader } from '@/components/PageHeader';
import { useConfirm, usePrompt } from '@/components/DialogProvider';
import { useToast } from '@/components/ToastProvider';
import { Button } from '@/components/ui/button';
import { ProviderEventsTable } from '@/components/finance/ProviderEventsTable';
import { AttachToObligationDialog, SettleProviderFactDialog } from '@/components/finance/RefundActionDialogs';
import { errorMessage } from '@/lib/errors';
import {
  financeProviderEventsQuery,
  financeSettingsQuery,
  settleStableOperation,
  useInvalidateFinance,
  useRefundAbandon,
  useRefundMatch,
  useRefundSplit,
  useRefundWriteOff,
  useResolveProviderFact,
  useStableOperationIds,
  useUnresolveProviderFact,
} from '@/lib/finance';
import { usePermissions } from '@/lib/me';
import { toMinor } from '@/lib/money';
import type { FinanceProviderFact, FinanceRefundAttempt } from '@/api/generated/types.gen';

export const Route = createFileRoute('/_app/finance/events')({ component: EventsPage });

// D-M6 (final review): the single number the query/paging/truncation-note
// below all agree on — changing the page size means changing it once.
const EVENTS_PAGE_LIMIT = 200;

function EventsPage() {
  return <EventsScreen />;
}

// Same route/screen split as finance.queue.tsx — a pure-props screen is
// testable without a mounted router.
export function EventsScreen() {
  const { can } = usePermissions();
  const { toast } = useToast();
  const askPrompt = usePrompt();
  const askConfirm = useConfirm();
  const canConfirm = can('finance.confirm');

  // D-I2 (final review, Ruling FR-1): the resolved list is a separate fetch
  // (PART 1's `processed` filter) — a mistaken Resolve is otherwise
  // unrecoverable from the UI (docs/30 §15 promises Unresolve). Only the
  // provider_queue section is ever non-empty with processed='resolved'
  // (PART 1's own contract). Empty sections are omitted; the resolved view
  // has its own empty state so it cannot imply that the open queue is clear.
  const [processed, setProcessed] = useState<'none' | 'resolved'>('none');
  // D-M6 (final review): no paging — `total` counts what THIS page
  // returned (PART 1's contract), capped at EVENTS_PAGE_LIMIT. Hitting the
  // cap is the only signal available that more may exist beyond it.
  const events = useQuery(financeProviderEventsQuery('all', EVENTS_PAGE_LIMIT, 0, processed));
  const { data: settings } = useQuery(financeSettingsQuery);
  const resolveFact = useResolveProviderFact();
  const unresolveFact = useUnresolveProviderFact();
  const refundMatch = useRefundMatch();
  const refundAbandon = useRefundAbandon();
  const refundSplit = useRefundSplit();
  const refundWriteOff = useRefundWriteOff();
  const [settlingFact, setSettlingFact] = useState<FinanceProviderFact | null>(null);
  const [attachingAttempt, setAttachingAttempt] = useState<FinanceRefundAttempt | null>(null);
  // Fix round 1, item 3 (T23-3): one stable operation_id PER TARGET across
  // this whole screen — every handler below used to mint a fresh
  // newOperationId() on every invocation, so a retry of the SAME logical
  // action (the operator clicking the same row's button again after a
  // failed request) got a NEW id, defeating idempotency exactly when it
  // matters (the first request may have actually reached the server).
  // get(key) mints once and reuses; clear(key) retires it on success so a
  // later, unrelated action on the same row starts fresh.
  const opIds = useStableOperationIds();
  // Fix round 2 (Critical): every mutation below is ONE hook shared by
  // every row on this screen — settleStableOperation's own doc comment
  // explains why clearing the key from a per-call mutate() onSuccess is
  // unsafe the moment two rows' actions overlap, and why mutateAsync's
  // returned promise fixes it.
  const invalidate = useInvalidateFinance();

  usePageHeader({
    subtitle: events.data
      ? `${events.data.total} items${events.data.total >= EVENTS_PAGE_LIMIT ? ` (showing the first ${EVENTS_PAGE_LIMIT} — more may exist)` : ''}`
      : undefined,
  });

  if (!can('finance.read') || errorStatus(events.error) === 403) return <AccessDenied />;

  async function onResolveFact(fact: FinanceProviderFact) {
    const resolution = await askPrompt({ title: 'Resolve this fact', label: 'Why will it never settle?' });
    if (!resolution?.trim()) return;
    const key = `resolve:${fact.kind}:${fact.id}`;
    const result = await settleStableOperation(
      resolveFact.mutateAsync({ kind: fact.kind, id: fact.id, resolution: resolution.trim(), operationId: opIds.get(key) }),
      key,
      opIds,
      invalidate,
    );
    if (!result.ok) toast({ kind: 'error', title: 'Could not resolve', message: result.message });
  }

  async function onUnresolveFact(fact: FinanceProviderFact) {
    const ok = await askConfirm({ title: 'Reopen this fact?', body: 'It returns to the provider queue, awaiting settle.' });
    if (!ok) return;
    const key = `unresolve:${fact.kind}:${fact.id}`;
    const result = await settleStableOperation(
      unresolveFact.mutateAsync({ kind: fact.kind, id: fact.id, operationId: opIds.get(key) }),
      key,
      opIds,
      invalidate,
    );
    if (!result.ok) toast({ kind: 'error', title: 'Could not reopen', message: result.message });
  }

  // "Match as ours" (unpaired) and "Found in console…" (stale, unknown) are
  // the SAME reconciliation — a provider-side ref found by hand — so they
  // share this handler and useRefundMatch, but NOT the same operation_id key
  // as onReread below any more (fix round 2, item 2): a lost response from
  // one must not lock the OTHER out via a shared key that never gets
  // cleared for whichever one didn't run.
  async function onMatchWithRef(attempt: FinanceRefundAttempt) {
    const ref = await askPrompt({ title: 'Provider reference', label: 'Refund resource id found in the provider console' });
    if (!ref?.trim()) return;
    const key = `match-ref:${attempt.id}`;
    const result = await settleStableOperation(
      refundMatch.mutateAsync({ attemptId: attempt.id, providerRef: ref.trim(), operationId: opIds.get(key) }),
      key,
      opIds,
      invalidate,
    );
    if (!result.ok) toast({ kind: 'error', title: 'Could not match', message: result.message });
  }

  async function onReread(attempt: FinanceRefundAttempt) {
    const key = `reread:${attempt.id}`;
    const result = await settleStableOperation(
      refundMatch.mutateAsync({ attemptId: attempt.id, operationId: opIds.get(key) }),
      key,
      opIds,
      invalidate,
    );
    if (!result.ok) toast({ kind: 'error', title: 'Could not re-read', message: result.message });
  }

  // Fix round 1, item 1 (Critical, Ruling T23-1): opens the real picker
  // dialog (AttachToObligationDialog, RefundActionDialogs.tsx) — GET
  // /finance/refunds?payment_id= now exists (Task 19's own fix round), so
  // the operator chooses an obligation of the SAME payment from a list
  // instead of typing an id nobody could ever actually see anywhere.
  function onAttach(attempt: FinanceRefundAttempt) {
    setAttachingAttempt(attempt);
  }

  async function onNeverExecuted(attempt: FinanceRefundAttempt) {
    const ok = await askConfirm({
      title: 'Mark as never executed?',
      body: 'The attempt is abandoned — record it only once the provider console confirms nothing happened.',
      destructive: true,
      confirmLabel: 'Never executed',
    });
    if (!ok) return;
    const reason = await askPrompt({ title: 'Why?', label: 'Reason' });
    if (!reason?.trim()) return;
    const key = `abandon:${attempt.id}`;
    const result = await settleStableOperation(
      refundAbandon.mutateAsync({ attemptId: attempt.id, reason: reason.trim(), operationId: opIds.get(key) }),
      key,
      opIds,
      invalidate,
    );
    if (!result.ok) toast({ kind: 'error', title: 'Could not abandon', message: result.message });
  }

  async function onSplit(attempt: FinanceRefundAttempt) {
    if (!attempt.refund_id) return;
    const refundId = attempt.refund_id;
    const txt = await askPrompt({
      title: 'Split into parts',
      label: `Parts in ${attempt.currency}, comma-separated (must sum to the obligation's amount)`,
      placeholder: '47.00, 70.00',
    });
    if (!txt) return;
    // Fix round 1, item 6 (review Minor): every token must parse — a
    // malformed one now names itself in the error rather than being
    // silently dropped from the sum (which used to just look like a wrong
    // total with no clue why).
    const tokens = txt.split(',').map((s) => s.trim());
    const parts: number[] = [];
    for (const [i, tok] of tokens.entries()) {
      const minor = toMinor(tok, attempt.currency);
      if (minor === null || minor <= 0) {
        toast({ kind: 'error', title: `Part ${i + 1} ("${tok}") is not a valid amount` });
        return;
      }
      parts.push(minor);
    }
    if (parts.length < 2) {
      toast({ kind: 'error', title: 'Enter at least two amounts, comma-separated' });
      return;
    }
    const key = `split:${refundId}`;
    const result = await settleStableOperation(
      refundSplit.mutateAsync({ refundId, partsMinor: parts, operationId: opIds.get(key) }),
      key,
      opIds,
      invalidate,
    );
    if (!result.ok) toast({ kind: 'error', title: 'Could not split', message: result.message });
  }

  async function onWriteOff(attempt: FinanceRefundAttempt) {
    if (!attempt.refund_id) return;
    const refundId = attempt.refund_id;
    const reason = await askPrompt({ title: 'Write off the rest?', label: 'Reason' });
    if (!reason?.trim()) return;
    const key = `writeoff:${refundId}`;
    const result = await settleStableOperation(
      refundWriteOff.mutateAsync({ refundId, reason: reason.trim(), operationId: opIds.get(key) }),
      key,
      opIds,
      invalidate,
    );
    if (!result.ok) toast({ kind: 'error', title: 'Could not write off', message: result.message });
  }

  return (
    <div className="flex min-w-0 flex-col gap-5">
      <header>
        <h2 className="text-2xl font-semibold tracking-tight text-[var(--text-heading)]">Reconciliation</h2>
        <p className="mt-1 max-w-2xl text-sm text-muted">Match provider payments and investigate refunds. Check your bank balance from the Accounts tab.</p>
      </header>
      <div role="group" aria-label="Reconciliation status" className="flex gap-2">
        <Button
          type="button"
          size="sm"
          variant={processed === 'none' ? 'primary' : 'secondary'}
          aria-pressed={processed === 'none'}
          onClick={() => setProcessed('none')}
        >
          Open
        </Button>
        <Button
          type="button"
          size="sm"
          variant={processed === 'resolved' ? 'primary' : 'secondary'}
          aria-pressed={processed === 'resolved'}
          onClick={() => setProcessed('resolved')}
        >
          Resolved
        </Button>
      </div>
      {events.data && events.data.total >= EVENTS_PAGE_LIMIT && (
        <p role="status" className="rounded-[var(--radius-control)] bg-[var(--surface-warm)] p-3 text-sm">Showing up to {EVENTS_PAGE_LIMIT} items. More may exist; review these items and refresh to continue.</p>
      )}
      {/* D-I3 (final review): isError must be checked BEFORE the loading
          branch (same order as finance.periods.tsx) — on an initial-load
          failure isLoading is false but data is still undefined, so
          `events.isLoading || !events.data` used to win and show an eternal
          spinner on the one screen meant to surface stuck refunds. */}
      {events.isError ? (
        <div role="alert" className="rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-5">
          <p className="font-semibold">Reconciliation could not be loaded</p>
          <p className="mt-1 text-sm text-destructive">{errorMessage(events.error)}</p>
          <Button className="mt-3" size="sm" variant="secondary" loading={events.isFetching} onClick={() => void events.refetch()}>Try again</Button>
        </div>
      ) : events.isLoading || !events.data ? (
        <p role="status" className="text-sm text-muted">Loading reconciliation…</p>
      ) : (
        <ProviderEventsTable
          data={events.data}
          processed={processed}
          canConfirm={canConfirm}
          incomeSideEnabled={settings?.income_side_enabled ?? false}
          onSettleFact={setSettlingFact}
          onResolveFact={(f) => void onResolveFact(f)}
          onUnresolveFact={(f) => void onUnresolveFact(f)}
          onMatchWithRef={(a) => void onMatchWithRef(a)}
          onReread={(a) => void onReread(a)}
          onAttach={onAttach}
          onNeverExecuted={(a) => void onNeverExecuted(a)}
          onSplit={(a) => void onSplit(a)}
          onWriteOff={(a) => void onWriteOff(a)}
        />
      )}
      <SettleProviderFactDialog
        fact={settlingFact}
        businessTz={settings?.business_tz}
        onClose={() => setSettlingFact(null)}
      />
      <AttachToObligationDialog attempt={attachingAttempt} onClose={() => setAttachingAttempt(null)} />
    </div>
  );
}
